import { Box, Code, Table, TableHead, TableBody, TableRow, TableCell, Text } from '@citibox-panel/ui';
import type { LockerLog } from '@citibox-panel/api-client';
import { parseLockerLogText } from '../lib/parseLockerLog';

// TODO: i18n — copy en ES hasta Fase 1.
const COLUMN_HEADERS = ['ACTOR', 'FECHA', 'HORA', 'REGISTRO'];

// Every metadata column is fixed; only the registry is fluid. Their content has a constant
// or bounded length, so sizing them as a share of the table would let them shrink below
// their own text on a narrow viewport. Sizing the actor as a share of the leftover was tried
// and dropped for that reason: any share small enough to leave the registry room was too
// narrow for a single short word, and one-word names broke mid-word.
//
// These three widths are deliberately tight — tighter than the text plus the cell's own
// horizontal padding, which is why the date and time carry `singleLine`: they overflow their
// column visibly rather than breaking mid-value, and a visible overflow is the failure mode
// we want over a silently split date. An actor name longer than one column wraps at its
// spaces, which is harmless.
//
// The registry takes whatever the three leave over. Written as a single subtraction rather
// than a percentage because in a fixed layout a bare percentage resolves against the TABLE
// width, not against the space the pixel columns leave free.
const ACTOR_COLUMN_PX = 120;
const DATE_COLUMN_PX = 100;
const TIME_COLUMN_PX = 80;
const FIXED_COLUMNS_PX = ACTOR_COLUMN_PX + DATE_COLUMN_PX + TIME_COLUMN_PX;

const COLUMN_WIDTHS = [
  `${String(ACTOR_COLUMN_PX)}px`,
  `${String(DATE_COLUMN_PX)}px`,
  `${String(TIME_COLUMN_PX)}px`,
  `calc(100% - ${String(FIXED_COLUMNS_PX)}px)`,
];

// Longest comfortable measure for a registry sentence: past it the eye loses the line on the way
// back to the next one, which a very wide viewport would otherwise produce.
const REGISTRY_MAX_MEASURE = '95ch';

export interface LockerLogTableProps {
  log: LockerLog;
}

// The log table: one block of rows per event (never interleaved), each STEPS paragraph split into
// descending sentence-rows that repeat the event's actor/date/time. Events are painted in the
// order delivered — the backend owns the ordering (newest-first) and the date/time formatting in
// the locker's local time, so this component neither sorts nor localizes. The repeated metadata
// keeps every row self-explanatory after a scroll, and carrying it a weight behind the registry is
// what stops three of four columns from competing with the one that holds the content. The step is
// weight only, never colour: the de-emphasized tone in the palette does not clear the contrast
// ratio body text needs, so every column keeps the body colour. Rows are told apart by the table's
// own hairlines, and the one under the pointer tints (`hover`): with the registry far to the right of
// a repeated actor/date/time, it is what keeps the eye on a single line across the width. Nothing
// here is clickable, so the tint is a reading aid, not an affordance — the header never takes it.
// Beyond a fixed height the Box scrolls internally with a sticky header; REGISTRO
// wraps fully (code values stay visible) up to a readable measure. An event's registry is
// third-party/system content carrying «Usuario» PII: it is rendered as inert plain text (default
// React escaping) — never as HTML, never auto-linked — so it cannot inject.
export function LockerLogTable({ log }: LockerLogTableProps) {
  return (
    <Box
      role="region"
      aria-label="Log del buzón"
      tabIndex={0}
      sx={{
        maxHeight: '50vh',
        overflow: 'auto',
        // No frame of its own: the codes block and this table share ONE hairline box, drawn by
        // the region wrapper in `DeliveryDetailCard`. A border here would be a box inside a box.
        // What separates the two zones inside that shared box is the codes block's own Divider.
        //
        // The row rules live on the cells, so they span whatever the table spans. Holding the
        // table off its container is what stops them short on both sides, so they read as the
        // table's own rules and not as lines drawn across the card.
        px: 1,
        // Twice the vertical padding `size="small"` gives a cell: the compact density packs
        // more rows in but leaves the sentences too tight to scan down. Horizontal padding is
        // left alone — the metadata columns are sized close to their text and cannot spare it.
        '& td, & th': { py: 1.5 },
        '& td': { overflowWrap: 'anywhere' },
        // A sticky header defaults to the page background, which reads as a grey band laid
        // over the card. Matching the card's surface removes the band — but it has to stay
        // opaque, not transparent: the body rows scroll underneath and would show through.
        '& th': { backgroundColor: 'background.paper' },
        // Every cell carries a bottom hairline to separate the rows; on the last row there is no
        // next row to separate, so it would just end the table on a trailing rule.
        '& tr:last-child td': { borderBottom: 0 },
      }}
    >
      <Table aria-label="Log del buzón" stickyHeader layout="fixed" columnWidths={COLUMN_WIDTHS}>
        <TableHead>
          <TableRow>
            {COLUMN_HEADERS.map((h) => (
              <TableCell key={h} component="th" scope="col">
                <Text variant="overline" color="secondary" dense bold>
                  {h}
                </Text>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {log.events.flatMap((event, eventIndex) => {
            // An absent actor arrives as an empty string (the wire field is not nullable).
            const actor = event.actor === '' ? '—' : event.actor;
            return parseLockerLogText(event.registry).map((segments, i) => (
              <TableRow key={`${String(eventIndex)}-${String(i)}`} hover>
                <TableCell>
                  <Text variant="body2">{actor}</Text>
                </TableCell>
                <TableCell singleLine>
                  <Text variant="body2">{event.date}</Text>
                </TableCell>
                <TableCell singleLine>
                  <Text variant="body2">{event.time}</Text>
                </TableCell>
                <TableCell>
                  {/* `subtitle2` is the medium weight that sets the registry ahead of the
                      metadata, but it defaults to an <h6>: forced back to a paragraph so a
                      long log does not inject dozens of phantom headings into the outline. */}
                  <Box sx={{ maxWidth: REGISTRY_MAX_MEASURE }}>
                    <Text variant="subtitle2" component="p">
                      {segments.length === 0
                        ? '—'
                        : segments.map((segment, s) =>
                            // The values the source had in quotes render as inline code; the prose
                            // between them stays a bare string, so the sentence is still one
                            // paragraph and not a run of chips. Index keys: the segments of a row
                            // are a fixed, positional decomposition of one immutable string.
                            segment.code ? (
                              <Code key={`${String(i)}-${String(s)}`}>{segment.text}</Code>
                            ) : (
                              segment.text
                            ),
                          )}
                    </Text>
                  </Box>
                </TableCell>
              </TableRow>
            ));
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
